import {Action, Store} from '@ngrx/store';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
    BeginGetVideoAction,
    BeginSelectNextVideoSceneAction,
    BeginUpdateVideoTimeAction,
    ChangeClipByTimeAction,
    ErrorVideoAction,
    GoBackwardAction,
    GoForwardAction,
    JumpToSceneAction,
    ReplayVideoAction,
    SetInitialVideoValues,
    SuccessGetVideoAction,
    UpdateNavigateTimeAction,
    UpdateVideoTimeAction,
    VideoEndedAction,
    VideoNavigateAction
} from '../actions/video.action';
import {map, mergeMap} from 'rxjs/operators';
import {VideoService} from '../../../services/video.service';
import {NavigationService} from '../../../services/navigation.service';
import {ErrorHandlerService} from '../../../services/errorHandler.service';
import {VideoUserVolumeUpdateAction} from '../actions/timeline.action';
import {VolumeService} from '../../../services/volume.service';
import {concatLatestFrom} from '@ngrx/operators';
import VideoState from '../video.state';
import {selectActualVideoState} from '../selectors/video.selector';

@Injectable()
export class VideoEffect {

    GetVideos$: Observable<Action> = createEffect(() => this.action$
        .pipe(
            ofType(BeginGetVideoAction),
            mergeMap(({params, videoId}) => this.videoService.getVideo(videoId, params))
        ));

    SelectFirstVideoAction$: Observable<Action> = createEffect(() => this.action$
        .pipe(
            ofType(SuccessGetVideoAction),
            map(action => this.videoService.setFirstClipAction(action.payload))
        ));

    AddSubmitToForms: Observable<Action> = createEffect(() => this.action$
        .pipe(
            ofType(SuccessGetVideoAction),
            map(action => this.videoService.addSubmitToForms(action.payload))
        ));

    UpdateVideoTimeAction$: Observable<Action> = createEffect(() => this.action$
        .pipe(
            ofType(BeginUpdateVideoTimeAction),
            map(action => action.payload),
            mergeMap(payload => this.videoService.setVideoTimeUpdate(payload))
        ));

    ChangeNextScene$: Observable<Action> = createEffect(() => this.action$
        .pipe(
            ofType(BeginSelectNextVideoSceneAction),
            map(action => action.payload),
            mergeMap(payload => this.videoService.setCurrentAndNextVideoScene(payload))
        ));

    VideoNavigate$: Observable<Action> = createEffect(() => this.action$
        .pipe(
            ofType(VideoNavigateAction),
            map(action => action),
            mergeMap((payload) => this.navigateService.navigateToTime(payload))));

    JumpToScene$: Observable<Action> = createEffect(() => this.action$
        .pipe(
            ofType(JumpToSceneAction),
            mergeMap((payload) => this.navigateService.jumpTo(payload.navigationTime, payload.videoState))));

    ChangeScene$: Observable<Action> = createEffect(() => this.action$
        .pipe(
            ofType(ChangeClipByTimeAction),
            map(value => value.navigationTime),
            mergeMap(time => this.navigateService.changeClipByNavigateTime(time))));

    JumpInTimeUpdate$: Observable<any> = createEffect(() => this.action$
        .pipe(
            ofType(UpdateNavigateTimeAction),
            map(value => UpdateVideoTimeAction({
                currentTime: value.navigationTime
            }))
        ));

    ReplayVideo$: Observable<any> = createEffect(() => this.action$
        .pipe(
            ofType(ReplayVideoAction),
            map(() => {
                this.videoService.replayButtonPressed.next(undefined);
                return UpdateNavigateTimeAction({
                    navigationTime: 0
                });
            })
        ));

    GoForward$: Observable<any> = createEffect(() => this.action$
        .pipe(
            ofType(GoForwardAction),
            concatLatestFrom(() => [
                this.videoService.calculateTimeForward(),
                this.store.select(selectActualVideoState)
            ]),
            map(([, navigationTime, actualState]) => VideoNavigateAction({
                navigationTime, videoState: actualState
            }))
        ));

    VideoEnded$: Observable<any> = createEffect(() => this.action$
        .pipe(
            ofType(VideoEndedAction),
            mergeMap(() => this.videoService.videoEndedActionHandler())));

    GoBackward$: Observable<any> = createEffect(() => this.action$
        .pipe(
            ofType(GoBackwardAction),
            mergeMap(() => this.videoService.calculateTimeBackward())
        ));

    SetInitialVolume$: Observable<Action> = createEffect(() => this.action$
        .pipe(
            ofType(SetInitialVideoValues),
            map(() => this.volumeService.setInitialUserVolume())
        ));

    SaveVolumeToLocalStorage$: Observable<any> = createEffect(() => this.action$
        .pipe(
            ofType(VideoUserVolumeUpdateAction),
            map((payload) => this.volumeService.saveVolumeToLocalStorage(payload.changedUserVolume))
        ), {
            dispatch: false
        });

    ErrorVideo$: Observable<any> = createEffect(() => this.action$
        .pipe(
            ofType(ErrorVideoAction),
            map((payload) => this.errorHandlerService.videoErrorAction(payload.errorType, payload.error))
        ), {
            dispatch: false
        });

    constructor(private action$: Actions,
        private videoService: VideoService,
        private volumeService: VolumeService,
        private navigateService: NavigationService,
        private errorHandlerService: ErrorHandlerService,
        private store: Store<{ video: VideoState }>
    ) {
    }
}
